import { HttpClient, HttpParams } from "@angular/common/http";
import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { DatetimeUtils } from "@app/shared/utils/datetime-utils";
import { OrderStatusGroupsDto } from "@dtos/values-dtos/order-status-groups-dto";
import { RestrictionsDto } from "@dtos/values-dtos/restrictions-dto";
import { LoggerService } from "@services/logger.service";
import { RequestHandlerService } from "@services/request-handler.service";
import { StoreAssignmentService } from "@services/store-assignment.service";
import { environment } from '@envs/environment';

type RestrictionLoadState = 'idle' | 'loading' | 'loaded' | 'error';

@Injectable({
    providedIn: 'root'
})
export class ValuesService {
    private _baseUrl = environment.apiUrl + 'values/';
    private readonly _http = inject(HttpClient);
    private readonly _logger = inject(LoggerService);
    private readonly _storeAssignmentService = inject(StoreAssignmentService);
    private readonly _req = inject(RequestHandlerService);

    isLoadingRestrictions = signal(false);
    isLoadingApiVersion = signal(false);
    isLoadingOrderStatusGroups = signal(false);

    restrictionLoadState = signal<RestrictionLoadState>('idle');
    restrictions = signal<RestrictionsDto[] | undefined>(undefined);
    apiVersion = signal<string | undefined>(undefined);
    orderStatusGroups = signal<OrderStatusGroupsDto | undefined>(undefined);
    isInActiveStoreHours = computed(() => {
        const storeNames = this._storeAssignmentService.assignableStores();
        if (!storeNames?.length) {
            return false;
        }

        if (this.restrictionLoadState() !== 'loaded') {
            return false;
        }

        const restrictions = this.restrictions();
        if (!restrictions?.length) {
            this._logger.debug("ValuesService>isInActiveStoreHours: allowing order because loaded restrictions were empty");
            return true;
        }

        const currentUtcPlus8Hours = DatetimeUtils.getManilaTime().hour;
        return this.isHourWithinAnyActiveStoreHour(currentUtcPlus8Hours);
    });

    constructor() {
        toObservable(this._storeAssignmentService.assignableStores)
            .pipe(takeUntilDestroyed())
            .subscribe(async (storeNames) => {
                this._logger.debug('ValuesService -- assignable stores changed:', storeNames);
                if (!storeNames?.length) {
                    this._logger.debug("ValuesService: storeNames was empty. clearing restrictions");
                    this.restrictions.set(undefined);
                    this.restrictionLoadState.set('idle');
                    return;
                }

                await this.loadRestrictionsAsync(storeNames);
            });
    }

    isHourWithinAnyActiveStoreHour(hour: number) {
        if (this.restrictionLoadState() !== 'loaded') return false;

        const restrictions = this.restrictions();
        if (!restrictions?.length) return true;

        return restrictions.some(x => hour >= x.serviceStartHour && hour < x.serviceEndHour);
    }

    getEarliestNextOrderHour() {
        if (this.restrictionLoadState() !== 'loaded') return undefined;

        const restrictions = this.restrictions();
        if (!restrictions?.length) return undefined;

        const currentHour = DatetimeUtils.getManilaTime().hour;
        const startHours = restrictions
            .map(x => x.serviceStartHour)
            .sort((a, b) => a - b);

        return startHours.find(x => x >= currentHour) ?? startHours[0];
    }

    async loadApiVersionAsync() {
        const request = this._http.get<{ version: string }>(this._baseUrl + 'ApiVersion');
        const response = await this._req.requesterHandler<{ version: string }>(request, this.isLoadingApiVersion);
        if (response.type === 'error') {
            this._logger.debug('ValuesService -- failed to load api version', response.error);
            return;
        }
        this.apiVersion.set(response.data.version);
    }

    async loadOrderStatusGroupsAsync() {
        if (this.orderStatusGroups()) return;
        const request = this._http.get<OrderStatusGroupsDto>(this._baseUrl + 'OrderStatusGroups');
        const response = await this._req.requesterHandler<OrderStatusGroupsDto>(request, this.isLoadingOrderStatusGroups);
        if (response.type === 'error') {
            this._logger.debug('ValuesService -- failed to load order status groups', response.error);
            return;
        }
        this.orderStatusGroups.set(response.data);
    }

    async loadRestrictionsAsync(storeNames?: string[]) {
        if (!storeNames?.length) {
            this.restrictions.set(undefined);
            this.restrictionLoadState.set('idle');
            return;
        }

        let params = new HttpParams();
        for (const store of storeNames) {
            params = params.append('stores', store);
        }

        this.restrictionLoadState.set('loading');

        const request = this._http.get<RestrictionsDto[]>(this._baseUrl + 'Restrictions', { params });
        const response = await this._req.requesterHandler<RestrictionsDto[]>(request, this.isLoadingRestrictions);
        if (response.type === 'error') {
            this._logger.debug('StoreAssignmentService -- error while updating assignable stores:', response.error);
            this.restrictions.set(undefined);
            this.restrictionLoadState.set('error');
            return;
        }

        const restrictions = response.data;
        this._logger.debug("ValuesService>loadRestrictionsAsync: got new restrictions", {restrictions});
        this.restrictions.set(restrictions);
        this.restrictionLoadState.set('loaded');
    }
}
